/*
Copyright 2026 Chain Care.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

export const PRENIGMA_COLLABORATION_NAMESPACE =
  "ai.prenigma.collaboration" as const;
export const PRENIGMA_COLLABORATION_VERSION = "v1" as const;
export const PRENIGMA_COLLABORATION_SOURCE_SYSTEM = "element_call" as const;
export const PRENIGMA_COLLABORATION_W1_001_DELIVERY_PROFILE =
  "source_emitted_unsigned_w1_001" as const;
export const PRENIGMA_COLLABORATION_SIGNATURE_PROFILE =
  "ed25519_jcs_sha256_v1" as const;
export const PRENIGMA_COLLABORATION_CANONICALIZATION_PROFILE =
  "RFC8785-JCS" as const;
export const PRENIGMA_COLLABORATION_DIGEST_PROFILE = "sha256" as const;

type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonObject | JsonValue[];
export interface JsonObject {
  [key: string]: JsonValue;
}

export type PrenigmaCollaborationEventName =
  | "session"
  | "command_receipt"
  | "transcript"
  | "barge_in"
  | "tts_cancel"
  | "no_transcript";

export type PrenigmaCollaborationCommandType =
  | "session.start"
  | "session.stop"
  | "media.mute"
  | "media.unmute"
  | "media.enable"
  | "media.disable"
  | "screenshare.start"
  | "screenshare.stop"
  | "session.cancel";

export type PrenigmaCollaborationCommandState =
  | "source_acknowledged"
  | "source_executed"
  | "failed"
  | "cancelled";

export type PrenigmaCollaborationSessionState =
  | "starting"
  | "running"
  | "stopping"
  | "stopped"
  | "failed"
  | "reconciliation_required";

export type PrenigmaNoTranscriptReason =
  | "consent_prohibits_persistence"
  | "policy_prohibits_persistence"
  | "classification_prohibits_persistence"
  | "retention_profile_disallows_text";

export interface PrenigmaCollaborationEnvelope {
  schema: string;
  event_id: string;
  event_type: PrenigmaCollaborationEventName;
  tenant_id: string;
  product_id: string;
  workspace_id: string;
  project_id: string;
  session_id: string;
  room_id: string;
  call_id: string;
  turn_id: string;
  workflow_id: string;
  workflow_run_id: string;
  source: {
    system: typeof PRENIGMA_COLLABORATION_SOURCE_SYSTEM;
    instance_id: string;
    producer_version: string;
    actor_type: "user_device" | "service";
    actor_id: string;
  };
  source_event_id: string;
  sequence: number;
  occurred_at: string;
  observed_at: string;
  causation_event_ids: string[];
  correlation_ids: string[];
  idempotency_key: string;
  classification: string;
  consent_ref: string;
  payload: JsonObject;
  integrity: JsonObject;
  metadata: JsonObject;
}

export interface PrenigmaCollaborationRuntimeEventSinkContext {
  matrixEventIds?: string[];
  matrixEventType?: string;
  matrixRoomId?: string;
  matrixReadbackVerified?: boolean;
}

export type PrenigmaCollaborationRuntimeEventSink = (
  event: PrenigmaCollaborationEnvelope,
  context?: PrenigmaCollaborationRuntimeEventSinkContext,
) => void | Promise<void>;

export interface PrenigmaCollaborationRuntimeOptions {
  tenantId: string;
  productId: string;
  sessionId: string;
  eventSink: PrenigmaCollaborationRuntimeEventSink;
  durableOutbox?: PrenigmaCollaborationRuntimeDurableOutbox;
  eventSigner?: PrenigmaCollaborationRuntimeEventSigner;
  workspaceId?: string;
  projectId?: string;
  callId?: string;
  workflowId?: string;
  workflowRunId?: string;
  collaborationProfile?: string;
  blueprintVersion?: string;
  sourceInstanceId?: string;
  producerVersion?: string;
  classification?: string;
  consentRef?: string;
  productSessionType?: string;
  idFactory?: (prefix: string) => string;
  now?: () => Date;
  diagnostics?: {
    exposeSourceProducer?: boolean;
  };
}

export interface PrenigmaCollaborationRuntimeEventSignature {
  keyId: string;
  signature: string;
  signatureProfile?: string;
  canonicalization?: string;
  digest?: string;
  payloadDigest?: string;
  verificationKeyRef?: string;
  signedAt?: string;
}

export interface PrenigmaCollaborationRuntimeEventSigner {
  /**
   * Sign one envelope. MAY return a promise.
   *
   * The synchronous form is kept because the existing live-evidence harness
   * uses it, but it should NOT be used in a real call: the only way to satisfy
   * a synchronous signer against a central-custody signing service is a
   * synchronous XHR, which blocks the main thread. During a call that stalls
   * rendering on every transcript, barge-in and command receipt, and can
   * disturb media handling. Browsers deprecate main-thread sync XHR and warn
   * on it.
   *
   * Returning a promise moves signing off the emit path: the envelope is
   * validated and written to the durable outbox first, and only reaches the
   * sink once the signature comes back. Delivery is already async — the sink's
   * return value is checked with isPromiseLike — so nothing downstream changes.
   */
  signEnvelope: (
    envelope: Readonly<PrenigmaCollaborationEnvelope>,
  ) =>
    | PrenigmaCollaborationRuntimeEventSignature
    | PromiseLike<PrenigmaCollaborationRuntimeEventSignature>;
}

export interface CreateElementCallCollaborationRuntimeProducerOptions extends PrenigmaCollaborationRuntimeOptions {
  roomId: string;
  userId: string;
  deviceId: string;
}

export interface EmitSessionStateOptions {
  state: PrenigmaCollaborationSessionState;
  startedAt?: string;
  endedAt?: string | null;
  mediaRuntimeSessionId?: string;
  causationEventIds?: string[];
  correlationIds?: string[];
}

export interface EmitSourceActionReceiptOptions {
  commandType: PrenigmaCollaborationCommandType;
  state: PrenigmaCollaborationCommandState;
  sourceStatus: "accepted" | "completed" | "failed" | "cancelled";
  commandId?: string;
  beforeState?: JsonObject;
  afterState?: JsonObject;
  failure?: JsonObject | null;
  causationEventIds?: string[];
  correlationIds?: string[];
}

export interface EmitTranscriptOptions {
  transcriptId: string;
  turnId: string;
  speaker: JsonObject;
  kind: "partial" | "final" | "correction" | "redacted";
  revision: number;
  language: string;
  startedAt: string;
  endedAt: string;
  provider: string;
  model: string;
  persistenceDecision: string;
  text?: string;
  consentRef?: string;
  supersedesTranscriptId?: string | null;
  stability?: number;
  redaction?: JsonObject;
  causationEventIds?: string[];
  correlationIds?: string[];
}

export interface EmitNoTranscriptOptions {
  turnId: string;
  speakerId: string;
  reason: PrenigmaNoTranscriptReason;
  consentRef: string;
  audioProcessed: boolean;
  policyVersion: string;
  causationEventIds?: string[];
  correlationIds?: string[];
}

export interface EmitBargeInOptions {
  bargeInId: string;
  obsoleteTurnId: string;
  supersedesTurnId: string;
  obsoleteTtsStreamRef: string;
  bargeInSourceEventRef: string;
  bargeInTimelineReadbackRef: string;
  sourceEventReadbackVerified: boolean;
  timelineReadbackVerified: boolean;
  partialIntentActionPolicy?: "warm_only" | "final_confirmed_only";
  partialTranscriptPersistence?: "ephemeral";
  causationEventIds?: string[];
  correlationIds?: string[];
}

export interface EmitTtsCancelOptions {
  cancelId: string;
  obsoleteTurnId: string;
  obsoleteTtsStreamRef: string;
  ttsCancelAckRef: string;
  bargeInSourceEventRef: string;
  bargeInTimelineReadbackRef: string;
  ttsCancelAcknowledged: boolean;
  obsoleteAudioSuppressed: boolean;
  sourceEventReadbackVerified: boolean;
  timelineReadbackVerified: boolean;
  causationEventIds?: string[];
  correlationIds?: string[];
}

export interface PrenigmaCollaborationRuntimeSourceProducer {
  emitSessionState: (
    options: EmitSessionStateOptions,
  ) => PrenigmaCollaborationEnvelope;
  emitSourceActionReceipt: (
    options: EmitSourceActionReceiptOptions,
  ) => PrenigmaCollaborationEnvelope;
  emitTranscript: (
    options: EmitTranscriptOptions,
  ) => PrenigmaCollaborationEnvelope;
  emitNoTranscript: (
    options: EmitNoTranscriptOptions,
  ) => PrenigmaCollaborationEnvelope;
  emitBargeIn: (options: EmitBargeInOptions) => PrenigmaCollaborationEnvelope;
  emitTtsCancel: (
    options: EmitTtsCancelOptions,
  ) => PrenigmaCollaborationEnvelope;
}

export interface PrenigmaCollaborationRuntimeDurableOutbox {
  enqueue: (event: PrenigmaCollaborationEnvelope) => void;
  readPending: () => PrenigmaCollaborationEnvelope[];
  markDelivered: (eventId: string) => void;
}

export interface PrenigmaCollaborationRuntimeStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

export interface CreateLocalStorageCollaborationRuntimeOutboxOptions {
  storage: PrenigmaCollaborationRuntimeStorage;
  storageKey: string;
  maxEvents?: number;
}

const SOURCE_OWNED_STATES = new Set<PrenigmaCollaborationCommandState>([
  "source_acknowledged",
  "source_executed",
]);

const NO_TRANSCRIPT_REASONS = new Set<PrenigmaNoTranscriptReason>([
  "consent_prohibits_persistence",
  "policy_prohibits_persistence",
  "classification_prohibits_persistence",
  "retention_profile_disallows_text",
]);

const SOURCE_EVENT_REF_PREFIX = "source_event_ref://";
const MATRIX_TIMELINE_REF_PREFIX = "matrix_timeline_ref://";
const TTS_STREAM_REF_PREFIX = "tts_stream_ref://";
const TTS_CANCEL_ACK_REF_PREFIX = "tts_cancel_ack_ref://";
const VOICE_CONTROL_FORBIDDEN_PAYLOAD_KEYS = new Set([
  "audio",
  "audio_bytes",
  "audio_b64",
  "audio_base64",
  "media",
  "raw_audio",
  "raw_audio_b64",
  "raw_audio_bytes",
  "raw_transcript",
  "raw_transcript_text",
  "transcript",
  "transcript_text",
  "provider_credentials",
]);

const SECRET_PATTERNS: Array<[RegExp, string]> = [
  [/sk-[A-Za-z0-9]{20,}/, "OpenAI-style API key"],
  [/AKIA[0-9A-Z]{16}/, "AWS access key"],
  [/xox[baprs]-[A-Za-z0-9-]{10,}/, "Slack token"],
  [/-----BEGIN (?:RSA |EC |OPENSSH )?PRIVATE KEY-----/, "private key"],
  [
    /(?:password|passwd|secret|api_key|access_token|refresh_token)\s*[=:]\s*["'][^"']{8,}["']/i,
    "credential assignment",
  ],
  [
    /(?:postgres|mysql|redis|mongodb):\/\/[^:\s]+:[^@\s]+@/i,
    "credentialed connection string",
  ],
];

export function collaborationEventSchema(
  eventName: PrenigmaCollaborationEventName,
): string {
  return `${PRENIGMA_COLLABORATION_NAMESPACE}.${eventName}.${PRENIGMA_COLLABORATION_VERSION}`;
}

export function canonicalPrenigmaCollaborationSourceSignaturePayload(
  envelope: Readonly<PrenigmaCollaborationEnvelope>,
): string {
  const payload = { ...envelope } as JsonObject;
  delete payload.integrity;
  const metadata =
    payload.metadata &&
    typeof payload.metadata === "object" &&
    !Array.isArray(payload.metadata)
      ? ({ ...(payload.metadata as JsonObject) } as JsonObject)
      : {};
  delete metadata.signature_status;
  if (Object.keys(metadata).length > 0) {
    payload.metadata = metadata;
  } else {
    delete payload.metadata;
  }
  return JSON.stringify(canonicalJsonValue(payload));
}

export function createElementCallCollaborationRuntimeSourceProducer({
  eventSink,
  durableOutbox,
  eventSigner,
  roomId,
  userId,
  deviceId,
  tenantId,
  productId,
  workspaceId = "",
  projectId = "",
  sessionId,
  callId = "",
  workflowId = "",
  workflowRunId = "",
  collaborationProfile = "element_call_source_v1",
  blueprintVersion = "",
  sourceInstanceId = `element-call:${roomId}:${deviceId}`,
  producerVersion = "dev",
  classification = "confidential",
  consentRef = "",
  productSessionType = "collaboration",
  idFactory = defaultIdFactory,
  now = (): Date => new Date(),
}: CreateElementCallCollaborationRuntimeProducerOptions): PrenigmaCollaborationRuntimeSourceProducer {
  requireText(tenantId, "tenantId");
  requireText(productId, "productId");
  requireText(sessionId, "sessionId");
  requireText(roomId, "roomId");
  requireText(userId, "userId");
  requireText(deviceId, "deviceId");

  let sequence = 0;
  const actorId = `${userId}:${deviceId}`;

  const emitEvent = (
    eventType: PrenigmaCollaborationEventName,
    payload: JsonObject,
    {
      turnId = "",
      causationEventIds = [],
      correlationIds = [],
      eventConsentRef = consentRef,
    }: {
      turnId?: string;
      causationEventIds?: string[];
      correlationIds?: string[];
      eventConsentRef?: string;
    } = {},
  ): PrenigmaCollaborationEnvelope => {
    const nextSequence = sequence++;
    const eventId = idFactory(`evt_${eventType}`);
    const sourceEventId = `${sourceInstanceId}:${nextSequence}:${eventType}`;
    const timestamp = now().toISOString();
    const envelope: PrenigmaCollaborationEnvelope = {
      schema: collaborationEventSchema(eventType),
      event_id: eventId,
      event_type: eventType,
      tenant_id: tenantId,
      product_id: productId,
      workspace_id: workspaceId,
      project_id: projectId,
      session_id: sessionId,
      room_id: roomId,
      call_id: callId,
      turn_id: turnId,
      workflow_id: workflowId,
      workflow_run_id: workflowRunId,
      source: {
        system: PRENIGMA_COLLABORATION_SOURCE_SYSTEM,
        instance_id: sourceInstanceId,
        producer_version: producerVersion,
        actor_type: "user_device",
        actor_id: actorId,
      },
      source_event_id: sourceEventId,
      sequence: nextSequence,
      occurred_at: timestamp,
      observed_at: timestamp,
      causation_event_ids: causationEventIds,
      correlation_ids: [...new Set([sessionId, roomId, ...correlationIds])],
      idempotency_key: `${tenantId}:${sourceInstanceId}:${sourceEventId}`,
      classification,
      consent_ref: eventConsentRef,
      payload,
      integrity: {},
      metadata: {
        delivery_profile: PRENIGMA_COLLABORATION_W1_001_DELIVERY_PROFILE,
        source_durable_outbox_status: durableOutbox
          ? "queued_before_sink"
          : "not_configured_w1_002_pending",
        signature_status: eventSigner
          ? "source_signing_required"
          : "not_signed_w1_002_pending",
        matrix_publication_status: "not_published_w1_002_pending",
      },
    };
    const deliver = (
      ready: PrenigmaCollaborationEnvelope,
    ): PrenigmaCollaborationEnvelope => {
      const delivery = eventSink(ready);
      if (durableOutbox && isPromiseLike(delivery)) {
        delivery
          .then(() => durableOutbox.markDelivered(ready.event_id))
          .catch(() => undefined);
      }
      return ready;
    };

    const withSignature = (
      signature: PrenigmaCollaborationRuntimeEventSignature,
    ): PrenigmaCollaborationEnvelope => ({
      ...envelope,
      integrity: buildIntegrity(signature),
      metadata: {
        ...envelope.metadata,
        signature_status: "source_signed",
      },
    });

    if (!eventSigner) {
      validateEnvelope(envelope);
      durableOutbox?.enqueue(envelope);
      return deliver(envelope);
    }

    const signed = eventSigner.signEnvelope(envelope);

    if (!isPromiseLike(signed)) {
      // Synchronous signer — behaviour is byte-for-byte what it always was.
      const ready = withSignature(signed);
      validateEnvelope(ready);
      durableOutbox?.enqueue(ready);
      return deliver(ready);
    }

    // Asynchronous signer. The envelope is validated and made durable BEFORE
    // the signature exists, so a signing failure or a reload mid-flight loses
    // the signature rather than the event. metadata.signature_status stays
    // "source_signing_required" in the outbox copy, which is the truth at that
    // moment; the copy that reaches the sink says "source_signed".
    validateEnvelope(envelope);
    durableOutbox?.enqueue(envelope);
    void Promise.resolve(signed)
      .then((signature) => deliver(withSignature(signature)))
      .catch(() => undefined);
    return envelope;
  };

  return {
    emitSessionState({
      state,
      startedAt,
      endedAt = null,
      mediaRuntimeSessionId = "",
      causationEventIds = [],
      correlationIds = [],
    }): PrenigmaCollaborationEnvelope {
      return emitEvent(
        "session",
        {
          session_id: sessionId,
          binding_version: 1,
          product_session_type: productSessionType,
          room_id: roomId,
          matrix_rtc_application: "m.call",
          matrix_rtc_slot_id: `m.call#${roomId}`,
          call_id: callId,
          media_runtime: "livekit",
          media_runtime_session_id: mediaRuntimeSessionId,
          workflow_id: workflowId,
          workflow_run_id: workflowRunId,
          state,
          started_at: startedAt ?? now().toISOString(),
          ended_at: endedAt,
          blueprint_version: blueprintVersion,
          collaboration_profile: collaborationProfile,
        },
        { causationEventIds, correlationIds },
      );
    },
    emitSourceActionReceipt({
      commandType,
      state,
      sourceStatus,
      commandId = idFactory(`cmd_${commandType.replaceAll(".", "_")}`),
      beforeState = {},
      afterState = {},
      failure = null,
      causationEventIds = [],
      correlationIds = [],
    }): PrenigmaCollaborationEnvelope {
      if (state === "source_executed" && Object.keys(afterState).length === 0) {
        throw new Error("source_executed receipts require afterState");
      }
      return emitEvent(
        "command_receipt",
        {
          command_id: commandId,
          command_type: commandType,
          receipt_id: idFactory("rcpt"),
          state,
          source_status: sourceStatus,
          acknowledged_at: now().toISOString(),
          executed_at: state === "source_executed" ? now().toISOString() : "",
          before_state: beforeState,
          after_state: afterState,
          failure,
        },
        { causationEventIds, correlationIds },
      );
    },
    emitTranscript({
      transcriptId,
      turnId,
      speaker,
      kind,
      revision,
      language,
      startedAt,
      endedAt,
      provider,
      model,
      persistenceDecision,
      text = "",
      consentRef: transcriptConsentRef = consentRef,
      supersedesTranscriptId = null,
      stability = kind === "final" ? 1 : 0,
      redaction = { state: "not_redacted" },
      causationEventIds = [],
      correlationIds = [],
    }): PrenigmaCollaborationEnvelope {
      if ((kind === "final" || kind === "correction") && !text.trim()) {
        throw new Error("final and correction transcript events require text");
      }
      if (
        (kind === "final" || kind === "correction") &&
        !transcriptConsentRef
      ) {
        throw new Error(
          "final and correction transcript events require consentRef",
        );
      }
      return emitEvent(
        "transcript",
        {
          transcript_id: transcriptId,
          turn_id: turnId,
          speaker,
          kind,
          revision,
          supersedes_transcript_id: supersedesTranscriptId,
          language,
          text,
          started_at: startedAt,
          ended_at: endedAt,
          stability,
          provider,
          model,
          consent_ref: transcriptConsentRef,
          persistence_decision: persistenceDecision,
          redaction,
        },
        {
          turnId,
          causationEventIds,
          correlationIds,
          eventConsentRef: transcriptConsentRef,
        },
      );
    },
    emitNoTranscript({
      turnId,
      speakerId,
      reason,
      consentRef: noTranscriptConsentRef,
      audioProcessed,
      policyVersion,
      causationEventIds = [],
      correlationIds = [],
    }): PrenigmaCollaborationEnvelope {
      if (!NO_TRANSCRIPT_REASONS.has(reason)) {
        throw new Error(`no_transcript reason is not registered: ${reason}`);
      }
      return emitEvent(
        "no_transcript",
        {
          turn_id: turnId,
          speaker_id: speakerId,
          reason,
          consent_ref: noTranscriptConsentRef,
          audio_processed: audioProcessed,
          transcript_text_persisted: false,
          policy_version: policyVersion,
        },
        {
          turnId,
          causationEventIds,
          correlationIds,
          eventConsentRef: noTranscriptConsentRef,
        },
      );
    },
    emitBargeIn({
      bargeInId,
      obsoleteTurnId,
      supersedesTurnId,
      obsoleteTtsStreamRef,
      bargeInSourceEventRef,
      bargeInTimelineReadbackRef,
      sourceEventReadbackVerified,
      timelineReadbackVerified,
      partialIntentActionPolicy = "warm_only",
      partialTranscriptPersistence = "ephemeral",
      causationEventIds = [],
      correlationIds = [],
    }): PrenigmaCollaborationEnvelope {
      return emitEvent(
        "barge_in",
        {
          barge_in_id: bargeInId,
          obsolete_turn_id: obsoleteTurnId,
          supersedes_turn_id: supersedesTurnId,
          obsolete_tts_stream_ref: obsoleteTtsStreamRef,
          barge_in_source_event_ref: bargeInSourceEventRef,
          barge_in_timeline_readback_ref: bargeInTimelineReadbackRef,
          source_event_readback_verified: sourceEventReadbackVerified,
          timeline_readback_verified: timelineReadbackVerified,
          partial_intent_action_policy: partialIntentActionPolicy,
          partial_transcript_persistence: partialTranscriptPersistence,
        },
        {
          turnId: supersedesTurnId,
          causationEventIds,
          correlationIds: [obsoleteTurnId, supersedesTurnId, ...correlationIds],
        },
      );
    },
    emitTtsCancel({
      cancelId,
      obsoleteTurnId,
      obsoleteTtsStreamRef,
      ttsCancelAckRef,
      bargeInSourceEventRef,
      bargeInTimelineReadbackRef,
      ttsCancelAcknowledged,
      obsoleteAudioSuppressed,
      sourceEventReadbackVerified,
      timelineReadbackVerified,
      causationEventIds = [],
      correlationIds = [],
    }): PrenigmaCollaborationEnvelope {
      return emitEvent(
        "tts_cancel",
        {
          cancel_id: cancelId,
          obsolete_turn_id: obsoleteTurnId,
          obsolete_tts_stream_ref: obsoleteTtsStreamRef,
          tts_cancel_ack_ref: ttsCancelAckRef,
          barge_in_source_event_ref: bargeInSourceEventRef,
          barge_in_timeline_readback_ref: bargeInTimelineReadbackRef,
          tts_cancel_acknowledged: ttsCancelAcknowledged,
          obsolete_audio_suppressed: obsoleteAudioSuppressed,
          source_event_readback_verified: sourceEventReadbackVerified,
          timeline_readback_verified: timelineReadbackVerified,
        },
        {
          turnId: obsoleteTurnId,
          causationEventIds,
          correlationIds: [obsoleteTurnId, ...correlationIds],
        },
      );
    },
  };
}

function buildIntegrity(
  signature: PrenigmaCollaborationRuntimeEventSignature,
): JsonObject {
  requireText(signature.keyId, "eventSignature.keyId");
  requireText(signature.signature, "eventSignature.signature");
  const integrity: JsonObject = {
    signature_profile:
      signature.signatureProfile ?? PRENIGMA_COLLABORATION_SIGNATURE_PROFILE,
    canonicalization:
      signature.canonicalization ??
      PRENIGMA_COLLABORATION_CANONICALIZATION_PROFILE,
    digest: signature.digest ?? PRENIGMA_COLLABORATION_DIGEST_PROFILE,
    key_id: signature.keyId,
    signature: signature.signature,
  };
  if (signature.payloadDigest) {
    integrity.payload_digest = signature.payloadDigest;
  }
  if (signature.verificationKeyRef) {
    integrity.verification_key_ref = signature.verificationKeyRef;
  }
  if (signature.signedAt) {
    integrity.signed_at = signature.signedAt;
  }
  return integrity;
}

function canonicalJsonValue(value: JsonValue | undefined): JsonValue {
  if (Array.isArray(value))
    return value.map((item) => canonicalJsonValue(item));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, item]) => item !== undefined)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, item]) => [key, canonicalJsonValue(item)]),
    );
  }
  return value ?? null;
}

export function createLocalStorageCollaborationRuntimeOutbox({
  storage,
  storageKey,
  maxEvents = 250,
}: CreateLocalStorageCollaborationRuntimeOutboxOptions): PrenigmaCollaborationRuntimeDurableOutbox {
  requireText(storageKey, "storageKey");
  if (maxEvents < 1) {
    throw new Error("maxEvents must be at least 1");
  }

  const readPending = (): PrenigmaCollaborationEnvelope[] => {
    const raw = storage.getItem(storageKey);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      throw new Error("collaboration runtime outbox payload must be an array");
    }
    const events = parsed.map((item) => asPrenigmaCollaborationEnvelope(item));
    for (const event of events) {
      validateEnvelope(event);
    }
    return events;
  };

  const writePending = (events: PrenigmaCollaborationEnvelope[]): void => {
    if (events.length === 0) {
      storage.removeItem(storageKey);
      return;
    }
    storage.setItem(storageKey, JSON.stringify(events));
  };

  return {
    enqueue(event: PrenigmaCollaborationEnvelope): void {
      validateEnvelope(event);
      const pending = readPending().filter(
        (candidate) =>
          candidate.idempotency_key !== event.idempotency_key &&
          candidate.event_id !== event.event_id,
      );
      pending.push(event);
      writePending(pending.slice(-maxEvents));
    },
    readPending,
    markDelivered(eventId: string): void {
      requireText(eventId, "eventId");
      writePending(readPending().filter((event) => event.event_id !== eventId));
    },
  };
}

function validateEnvelope(envelope: PrenigmaCollaborationEnvelope): void {
  requireText(envelope.event_id, "event_id");
  requireText(envelope.tenant_id, "tenant_id");
  requireText(envelope.product_id, "product_id");
  requireText(envelope.session_id, "session_id");
  requireText(envelope.room_id, "room_id");
  requireText(envelope.source.instance_id, "source.instance_id");
  requireText(envelope.source.producer_version, "source.producer_version");
  requireText(envelope.source.actor_id, "source.actor_id");
  requireText(envelope.source_event_id, "source_event_id");
  if (envelope.schema !== collaborationEventSchema(envelope.event_type)) {
    throw new Error("event schema must match event_type");
  }
  if (
    envelope.event_type === "command_receipt" &&
    SOURCE_OWNED_STATES.has(
      envelope.payload.state as PrenigmaCollaborationCommandState,
    ) &&
    !envelope.source_event_id
  ) {
    throw new Error("source-owned receipts require source_event_id");
  }
  if (envelope.event_type === "no_transcript") {
    for (const forbiddenKey of [
      "text",
      "transcript_text",
      "transcript_hash",
      "text_hash",
    ]) {
      if (envelope.payload[forbiddenKey]) {
        throw new Error("no_transcript events must not include text or hashes");
      }
    }
  }
  if (envelope.event_type === "barge_in") {
    validateBargeInPayload(envelope.payload);
  }
  if (envelope.event_type === "tts_cancel") {
    validateTtsCancelPayload(envelope.payload);
  }
  const finding = scanSecretLikeValues(envelope);
  if (finding) {
    throw new Error(
      `secret-like value detected at ${finding.path}: ${finding.kind}`,
    );
  }
}

function validateBargeInPayload(payload: JsonObject): void {
  assertNoVoiceControlRawMaterial(payload);
  requireText(payloadText(payload.barge_in_id), "payload.barge_in_id");
  requireText(
    payloadText(payload.obsolete_turn_id),
    "payload.obsolete_turn_id",
  );
  requireText(
    payloadText(payload.supersedes_turn_id),
    "payload.supersedes_turn_id",
  );
  requirePrefixedRef(payload, "obsolete_tts_stream_ref", TTS_STREAM_REF_PREFIX);
  requirePrefixedRef(
    payload,
    "barge_in_source_event_ref",
    SOURCE_EVENT_REF_PREFIX,
  );
  requirePrefixedRef(
    payload,
    "barge_in_timeline_readback_ref",
    MATRIX_TIMELINE_REF_PREFIX,
  );
  requirePayloadTrue(payload, "source_event_readback_verified");
  requirePayloadTrue(payload, "timeline_readback_verified");
}

function validateTtsCancelPayload(payload: JsonObject): void {
  assertNoVoiceControlRawMaterial(payload);
  requireText(payloadText(payload.cancel_id), "payload.cancel_id");
  requireText(
    payloadText(payload.obsolete_turn_id),
    "payload.obsolete_turn_id",
  );
  requirePrefixedRef(payload, "obsolete_tts_stream_ref", TTS_STREAM_REF_PREFIX);
  requirePrefixedRef(payload, "tts_cancel_ack_ref", TTS_CANCEL_ACK_REF_PREFIX);
  requirePrefixedRef(
    payload,
    "barge_in_source_event_ref",
    SOURCE_EVENT_REF_PREFIX,
  );
  requirePrefixedRef(
    payload,
    "barge_in_timeline_readback_ref",
    MATRIX_TIMELINE_REF_PREFIX,
  );
  requirePayloadTrue(payload, "tts_cancel_acknowledged");
  requirePayloadTrue(payload, "obsolete_audio_suppressed");
  requirePayloadTrue(payload, "source_event_readback_verified");
  requirePayloadTrue(payload, "timeline_readback_verified");
}

function requirePrefixedRef(
  payload: JsonObject,
  fieldName: string,
  prefix: string,
): void {
  const value = payloadText(payload[fieldName]);
  requireText(value, `payload.${fieldName}`);
  if (!value.startsWith(prefix)) {
    throw new Error(`payload.${fieldName} must start with ${prefix}`);
  }
}

function requirePayloadTrue(payload: JsonObject, fieldName: string): void {
  if (payload[fieldName] !== true) {
    throw new Error(`payload.${fieldName} must be verified`);
  }
}

function asPrenigmaCollaborationEnvelope(
  value: unknown,
): PrenigmaCollaborationEnvelope {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("collaboration runtime outbox item must be an object");
  }
  return value as PrenigmaCollaborationEnvelope;
}

function requireText(value: string, path: string): void {
  if (!value.trim()) {
    throw new Error(`${path} is required`);
  }
}

function payloadText(value: JsonValue | undefined): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
}

function assertNoVoiceControlRawMaterial(
  value: JsonValue,
  path = "payload",
): void {
  if (Array.isArray(value)) {
    for (const [index, item] of value.entries()) {
      assertNoVoiceControlRawMaterial(item, `${path}[${index}]`);
    }
    return;
  }
  if (value === null || typeof value !== "object") return;
  for (const [key, item] of Object.entries(value)) {
    const childPath = `${path}.${key}`;
    if (
      VOICE_CONTROL_FORBIDDEN_PAYLOAD_KEYS.has(key.toLowerCase()) &&
      carriesMaterial(item)
    ) {
      throw new Error(
        `voice control event must not carry raw material at ${childPath}`,
      );
    }
    assertNoVoiceControlRawMaterial(item, childPath);
  }
}

function carriesMaterial(value: JsonValue): boolean {
  if (value === null || value === "" || value === false) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}

function isPromiseLike<T>(value: T | PromiseLike<T>): value is PromiseLike<T> {
  return (
    value !== null &&
    typeof value === "object" &&
    typeof (value as { then?: unknown }).then === "function"
  );
}

function defaultIdFactory(prefix: string): string {
  const id =
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
  return `${prefix}_${id.replaceAll("-", "_")}`;
}

function scanSecretLikeValues(
  value: JsonValue | PrenigmaCollaborationEnvelope,
  path = "$",
): { path: string; kind: string } | null {
  if (Array.isArray(value)) {
    for (const [index, item] of value.entries()) {
      const finding = scanSecretLikeValues(item, `${path}[${index}]`);
      if (finding) return finding;
    }
  } else if (value !== null && typeof value === "object") {
    for (const [key, item] of Object.entries(value)) {
      const finding = scanSecretLikeValues(item as JsonValue, `${path}.${key}`);
      if (finding) return finding;
    }
  } else if (typeof value === "string") {
    for (const [pattern, kind] of SECRET_PATTERNS) {
      if (pattern.test(value)) return { path, kind };
    }
  }
  return null;
}
